import { escapeHtml } from '../ui.js';
import { listenWaMessages, listenWaThreads, waNumbers } from '../db.js';
import { toDate, windowState, maskPhone } from '../wa-core.js';
import { mailRowHtml, isAutoReply } from './mail-reader.js';
import { startOfWeek } from './mail-correspondence.js';
import { chMark, markOf, senderName, photosHtml, voiceHtml, wireMedia, bunch, savedLine, recordedHtml, stopVoice } from './wa-ui.js';
import { openOverlay } from './overlay.js';

// A PROJECT'S CORRESPONDENCE (board 1, 17/10/2026): the Emails tab renamed,
// one timeline in date order, This week / Last week / Earlier, mixing the
// archiver's TRACK// email rows (drawn by mail-reader's own row, unchanged,
// opening the shared reader as before) with the WhatsApp messages filed to the
// project — RECEIVED / SENT with the green channel marker (or FORWARDED),
// photos as Drive thumbnails, voice notes with play and transcript, where the
// photos went, the 24-hour window when a reply is owed, why it was filed here,
// and what it was recorded as. Filters All / Email / WhatsApp / Photos.

const FILTERS = ['All', 'Email', 'WhatsApp', 'Photos'];

/** The words under a WhatsApp sender's name on the timeline. */
function detailOf(b, thread, ctx) {
  const m = b.lead;
  if (m.direction === 'out') {
    const to = senderName({ direction: 'in', from: m.to || {} }, thread);
    const bits = [`to ${to}`, m.sentFrom === 'phone' ? 'from the phone' : 'from Track'];
    if (m.type === 'template' && m.templateName) bits.push(`template · ${String(m.templateName).replace(/_/g, ' ')}`);
    if (m.waStatus === 'read') bits.push('read');
    else if (m.waStatus === 'delivered') bits.push('delivered');
    else if (m.waStatus === 'failed') bits.push('not delivered');
    return bits.join(' · ');
  }
  if (m.source === 'forward') return `WhatsApp forwarded to Track by ${String((m.forwardedBy && m.forwardedBy.name) || 'staff').split(' ')[0]}`;
  if (b.kind === 'audio') return `Voice note${m.media && m.media.duration ? ` · ${Math.floor(m.media.duration / 60)}:${String(Math.round(m.media.duration % 60)).padStart(2, '0')}` : ''}`;
  if (m.source === 'import') return `From the imported chat${thread && thread.displayName ? ` “${thread.displayName}”` : ''}`;
  const c = (thread && thread.contact) || {};
  const co = c.clientId && ctx.clientId === c.clientId ? ctx.clientName : (c.company || '');
  const line = [co, c.role].filter(Boolean).join(' · ');
  return line || (m.from && m.from.waId ? maskPhone(`+${m.from.waId}`) : '');
}

const stamp = (d) => (d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '');

function waRowHtml(b, thread, ctx, awaiting) {
  const m = b.lead;
  const out = m.direction === 'out';
  const body = String(m.body || b.caption || m.caption || '').trim();
  const w = awaiting ? windowState(thread.lastInboundAt) : null;
  const meta = [];
  const saved = b.photos.length ? savedLine(b.photos.filter((x) => x.media && x.media.driveFileId), ctx.projectNo, m.at) : '';
  if (saved) meta.push(`<span>${escapeHtml(saved)}</span>`);
  if (w) meta.push(`<span class="wa-await">${escapeHtml(`Awaiting reply · window open ${w.hoursLeft}h`)}</span>`);
  const why = !out ? String(m.matchReason || (thread && thread.matchReason) || '').trim() : '';
  if (why) meta.push(`<span class="wa-why">${escapeHtml(why)}</span>`);
  const rec = m.recordedAs && m.recordedAs.kind === 'instruction'
    ? recordedHtml(m, { action: '<button type="button" class="mq-link wa-rec-act" data-wa-reply="1">Confirm with client</button>' })
    : recordedHtml(m);
  return `<div class="mrw-row wa-row${w ? ' is-attn' : ''}" data-wa="${escapeHtml(m.id)}" data-thread="${escapeHtml(m.threadId || '')}" role="button" tabindex="0">
      <span class="mrw-dir${out ? ' sent' : ''}">${out ? 'Sent' : 'Received'}${chMark(markOf(m), { sent: out })}</span>
      <div class="mrw-main">
        <div class="mrw-l1">
          <span class="mrw-name">${escapeHtml(senderName(m, thread))}</span>
          <span class="mrw-detail">${escapeHtml(detailOf(b, thread, ctx))}</span>
          <span class="mrw-date">${escapeHtml(stamp(toDate(m.at)))}</span>
        </div>
        ${b.kind === 'audio' ? voiceHtml(m) : ''}
        ${body && b.kind !== 'audio' ? `<div class="wa-body${out || m.source === 'forward' ? ' is-quiet' : ''}">${escapeHtml(body)}</div>` : ''}
        ${b.kind === 'document' || b.kind === 'video' ? `<div class="wa-doc">${escapeHtml((m.media && m.media.filename) || (b.kind === 'video' ? 'Video' : 'Document'))}</div>` : ''}
        ${photosHtml(b.photos)}
        ${meta.length ? `<div class="wa-meta">${meta.join('')}</div>` : ''}
        ${rec}
      </div>
    </div>`;
}

/**
 * Mount the Correspondence panel into `host`.
 *   project    the project record (id, number, clientRef…)
 *   clientName the project's client (for "[Contractor] · site contact")
 *   emails     archiver entries (normArchiver) — setEmails() when they arrive
 *   onOpenEmail(entry)  the existing shared reader
 *   onCount(n)  the tab's count: emails + WhatsApp
 *   onImport()  "Import a WhatsApp chat…"
 *   whatsapp    false → emails only, as before WhatsApp (wa-access.js gate: not released to this user)
 * Returns { setEmails(entries), knownIds(), destroy() }.
 */
export function mountCorrespondence(host, { project, session = null, clientName = '', clientId = '', emails = null, onOpenEmail, onCount, onImport, whatsapp = true } = {}) {
  if (!whatsapp) onImport = null;
  const st = { emails, msgs: null, threads: new Map(), filter: 'All', numbers: [], failed: false };
  const ctx = { projectNo: String(project.number || ''), clientName, clientId };
  if (whatsapp) waNumbers().then((n) => { st.numbers = n || []; }).catch(() => {});

  const unMsgs = (whatsapp && project.id) ? listenWaMessages({ projectId: project.id }, (rows) => {
    if (!host.isConnected) { unMsgs(); return; }
    if (rows == null) { st.failed = true; st.msgs = st.msgs || []; } else { st.failed = false; st.msgs = rows; }
    paint();
  }) : (() => { st.msgs = []; return () => {}; })();
  const unThreads = (whatsapp && project.id) ? listenWaThreads((rows) => {
    if (!host.isConnected) { unThreads(); return; }
    if (rows) st.threads = new Map(rows.map((t) => [t.id, t]));
    paint();
  }, { projectId: project.id }) : () => {};

  function model() {
    const emailsList = (st.emails || []).map((e) => ({ k: 'email', at: e.date ? e.date.getTime() : 0, e }));
    const shown = (st.msgs || []).filter((m) => m.type !== 'reaction' && m.type !== 'system');
    const bunches = bunch(shown);
    // The newest thing they sent on each thread, when nobody has answered it yet.
    const awaiting = new Set();
    st.threads.forEach((t) => {
      // Only where a reply is owed AND can still go freely: an open window on a
      // chat with a person (imports and forwards are records, not conversations).
      if (t.lastDirection !== 'in' || t.kind === 'import' || t.kind === 'forward' || !windowState(t.lastInboundAt).open) return;
      const last = [...bunches].reverse().find((b) => b.lead.threadId === t.id && b.lead.direction === 'in');
      if (last) awaiting.add(last.lead.id);
    });
    const was = bunches.map((b) => ({ k: 'wa', at: (toDate(b.lead.at) || new Date(0)).getTime(), b }));
    const photos = was.filter((x) => x.b.photos.length);
    const counts = { All: emailsList.length + was.length, Email: emailsList.length, WhatsApp: was.length, Photos: photos.reduce((s, x) => s + x.b.photos.length, 0) };
    let items = st.filter === 'Email' ? emailsList : st.filter === 'WhatsApp' ? was : st.filter === 'Photos' ? photos : emailsList.concat(was);
    items = items.slice().sort((a, b) => b.at - a.at);
    return { items, counts, awaiting };
  }

  function paint() {
    if (!host.isConnected) return;
    const { items, counts, awaiting } = model();
    if (onCount) onCount(st.emails == null && st.msgs == null ? null : counts.All);
    const flat = [];
    const thisWeek = startOfWeek(new Date()).getTime();
    const lastWeek = thisWeek - 7 * 86400000;
    const buckets = { 'This week': [], 'Last week': [], Earlier: [] };
    items.forEach((x) => (x.at >= thisWeek ? buckets['This week'] : x.at >= lastWeek ? buckets['Last week'] : buckets.Earlier).push(x));
    const bucketHtml = Object.entries(buckets).filter(([, list]) => list.length).map(([name, list]) => {
      const autos = list.filter((x) => x.k === 'email' && isAutoReply(x.e));
      const rows = list.filter((x) => !(x.k === 'email' && isAutoReply(x.e)));
      const idx = (e) => { flat.push(e); return flat.length - 1; };
      const group = autos.length ? `<div class="mrw-auto"><span class="mrw-pill">Auto · ${autos.length}</span>
          <span class="mrw-autonames">${escapeHtml(autos.map((x) => `${x.e.senderName || x.e.email || 'Someone'} — automatic reply`).join(' · '))}</span>
          <button type="button" class="mrw-show" aria-expanded="false">Show</button></div>
          <div class="mrw-autorows" hidden>${autos.map((x) => mailRowHtml(x.e, idx(x.e))).join('')}</div>` : '';
      return `<section class="mrw-bucket"><div class="eyebrow blue mrw-label">${escapeHtml(name)}</div>
        <div class="mrw-card">${group}${rows.map((x) => (x.k === 'email' ? mailRowHtml(x.e, idx(x.e)) : waRowHtml(x.b, st.threads.get(x.b.lead.threadId) || {}, ctx, awaiting.has(x.b.lead.id)))).join('')}</div></section>`;
    }).join('');
    const loading = st.emails == null && st.msgs == null;
    const empty = !loading && !items.length;
    host.innerHTML = `
      <div class="mrw-head wa-head">
        <div class="wa-head-l">
          <div class="eyebrow blue">${whatsapp ? 'Correspondence' : 'Emails'}</div>
          <div class="g-cap">Emails tagged <span class="g-mono">TRACK// ${escapeHtml(ctx.projectNo)}</span>${whatsapp ? ' and WhatsApp filed to this project' : ''}. Shared, everyone sees the same.</div>
        </div>
        <div class="wa-head-r">
          ${whatsapp ? `<div class="mq-chips" role="group" aria-label="Show">${FILTERS.map((f) => `<button type="button" class="mq-chip${st.filter === f ? ' is-on' : ''}" data-filter="${f}" aria-pressed="${st.filter === f}">${f}${counts[f] ? ` <span class="mq-chip-n">${counts[f]}</span>` : ''}</button>`).join('')}</div>` : ''}
          ${onImport ? '<button type="button" class="mq-link wa-import-link" data-import="1">Import a WhatsApp chat…</button>' : ''}
        </div>
      </div>
      ${st.failed ? '<p class="g-note">WhatsApp messages could not be read just now; the emails below are complete.</p>' : ''}
      ${loading ? '<p class="g-note">Loading…</p>' : empty ? `<p class="g-note">${escapeHtml(!whatsapp ? `No emails tagged TRACK// ${ctx.projectNo} yet.` : st.filter === 'All'
        ? `Nothing filed to ${ctx.projectNo} yet. Email tagged TRACK// ${ctx.projectNo}, WhatsApp on the ASP number from the project’s contacts, and chats you import all collect here.`
        : st.filter === 'Photos' ? 'No WhatsApp photos filed to this project yet.' : `No ${st.filter === 'Email' ? 'emails' : 'WhatsApp messages'} filed to this project yet.`)}</p>`
        : `<div class="mrw">${bucketHtml}</div>`}`;
    host._waFlat = flat;
    wireMedia(host);
  }

  function openThread(threadId, { reply = false } = {}) {
    const t = st.threads.get(threadId);
    if (!t) return;
    const { content, close, onClose } = openOverlay({ title: `WhatsApp · ${t.displayName || (t.contact && t.contact.name) || ''}`, subtitle: `${ctx.projectNo} · Correspondence`, wide: true });
    content.classList.add('wa-ov');
    import('./wa-reader.js').then(({ openWaReader }) => {
      const r = openWaReader(content, {
        thread: t, project, session, numbers: st.numbers, overlay: true,
        on: {
          toast: (msg) => { const n = content.querySelector('.wa-ov-note') || content.insertAdjacentElement('afterbegin', Object.assign(document.createElement('div'), { className: 'wa-ov-note' })); n.textContent = msg; },
          emailInstead: ({ text }) => { close(); import('./mail-compose.js').then((mc) => mc.renderCompose(session, { mode: 'new', job: ctx.projectNo, project, body: text })); },
        },
      });
      onClose(() => r.destroy());
      if (reply) setTimeout(() => { const ta = content.querySelector('textarea, .wa-tpl.is-on'); if (ta) ta.focus(); }, 300);
    });
    onClose(stopVoice);
  }

  host.addEventListener('click', (e) => {
    const f = e.target.closest('[data-filter]');
    if (f) { st.filter = f.dataset.filter; paint(); return; }
    if (e.target.closest('[data-import]')) { if (onImport) onImport(); return; }
    const show = e.target.closest('.mrw-show');
    if (show) {
      const rows = show.closest('.mrw-auto').nextElementSibling;
      const on = rows.hidden; rows.hidden = !on; show.textContent = on ? 'Hide' : 'Show'; show.setAttribute('aria-expanded', String(on));
      return;
    }
    if (e.target.closest('.wa-play, .wa-ph, a')) return;   // wa-ui plays / opens these
    const wa = e.target.closest('.wa-row');
    if (wa) { openThread(wa.dataset.thread, { reply: !!e.target.closest('[data-wa-reply]') }); return; }
    const row = e.target.closest('.mrw-row[data-i]');
    if (row && onOpenEmail) onOpenEmail((host._waFlat || [])[Number(row.dataset.i)]);
  });
  host.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (e.target.closest('button, a, input')) return;
    const row = e.target.closest('.mrw-row');
    if (!row) return;
    e.preventDefault();
    if (row.classList.contains('wa-row')) openThread(row.dataset.thread);
    else if (onOpenEmail) onOpenEmail((host._waFlat || [])[Number(row.dataset.i)]);
  });

  paint();
  return {
    setEmails(list) { st.emails = list || []; paint(); },
    /** Message ids already filed to the project — the import skips them. */
    knownIds: () => new Set((st.msgs || []).map((m) => m.id)),
    messages: () => st.msgs || [],
    /** The people already on WhatsApp with this project (the import matches against them). */
    contacts: () => [...st.threads.values()].filter((t) => t.contact && t.contact.name).map((t) => ({ name: t.contact.name, role: t.contact.role, clientId: t.contact.clientId, waId: t.waId })),
    destroy() { try { unMsgs(); unThreads(); } catch (_) { /* gone */ } stopVoice(); },
  };
}
